import {
  accountSearchColumns,
  boPhanSearchColumns,
  chiPhiKhongHopLeSearchColumns,
  dotThanhToanSearchColumns,
  hopDongSearchColumns,
  kheUocSearchColumns,
  khoHangSearchColumns,
  lenhSanXuatSearchColumns,
  lyDoNhapXuatSearchColumns,
  phiSearchColumns,
  QUERY_KEYS,
  vatTu1SearchColumns,
  viTriSearchColumns,
  vuViecSearchColumns,
  warehouseSearchColumns
} from '@/constants';
import {
  AccountModel,
  BoPhan,
  ChiPhiKhongHopLeData,
  DotThanhToan,
  HopDong,
  KheUoc,
  KhoHang,
  NhapXuat,
  Phi,
  PhieuXuatDieuChuyenCellValue,
  PhieuXuatDieuChuyenChiTiet,
  PhieuXuatDieuChuyenLenhSanXuatData,
  VatTu,
  VuViec
} from '@/types/schemas';
import type { DataTableColumn, DataTableRenderCellParams as GridRenderCellParams } from '@/components/custom/erp';
import { CellFieldV2 as CellField, DvtField, SearchField } from '@/components/custom/erp';
import { ViTriKho } from '@/types/schemas/vi-tri-kho.type';
import { Checkbox } from '@/components/ui/checkbox';
import { FormMode } from '@/types/form';

type DetailTableColumn = DataTableColumn<PhieuXuatDieuChuyenChiTiet> & {
  pasteConfig?: {
    lookupKey: string;
    resultField: string;
    detailEndpoint: string;
  };
};

type DetailCellParams = GridRenderCellParams<PhieuXuatDieuChuyenChiTiet>;

export const getDetailTableColumns = (
  onCellValueChange: (rowUuid: string, field: string, newValue: PhieuXuatDieuChuyenCellValue) => void,
  fetchDetail: (params: string) => Promise<unknown>,
  formMode?: FormMode
): DetailTableColumn[] => {
  const isDisabled = formMode === 'view';
  return [
    // Vật tư fields
    {
      field: 'ma_vt',
      headerName: 'Mã sản phẩm',
      width: 200,
      renderCell: (params: DetailCellParams) => (
        <SearchField<VatTu>
          type='text'
          searchEndpoint={`/${QUERY_KEYS.VAT_TU}/`}
          searchColumns={vatTu1SearchColumns}
          columnDisplay='ma_vt'
          dialogTitle='Danh mục vật tư'
          value={params.row.ma_vt_data?.ma_vt || ''}
          onRowSelection={async (row: VatTu) => {
            const data = (await fetchDetail(row?.uuid || '')) as PhieuXuatDieuChuyenCellValue;
            onCellValueChange(params.row.uuid, 'ma_vt_data', data);
          }}
          className='w-full'
          disabled={isDisabled}
        />
      ),
      pasteConfig: {
        lookupKey: 'ma_vt',
        resultField: 'ma_vt_data',
        detailEndpoint: QUERY_KEYS.VAT_TU
      }
    },
    {
      field: 'ten_vt',
      headerName: 'Tên vật tư',
      width: 200,
      renderCell: (params: DetailCellParams) => (
        <CellField name='ten_vt' type='text' value={params.row.ma_vt_data?.ten_vt || ''} disabled={isDisabled} />
      )
    },
    {
      field: 'dvt',
      headerName: 'Đvt',
      width: 120,
      renderCell: (params: DetailCellParams) => (
        <DvtField row={params.row} onCellValueChange={onCellValueChange} disabled={isDisabled} />
      )
    },
    {
      field: 'ma_khon',
      headerName: 'Kho nhận',
      width: 150,
      renderCell: (params: DetailCellParams) => (
        <SearchField<KhoHang>
          type='text'
          searchEndpoint={`/${QUERY_KEYS.KHO_HANG}/`}
          searchColumns={khoHangSearchColumns}
          columnDisplay='ma_kho'
          dialogTitle='Danh mục kho hàng'
          value={params.row.ma_khon_data?.ma_kho || ''}
          onRowSelection={(row: KhoHang) => onCellValueChange(params.row.uuid, 'ma_khon_data', row)}
          className='w-full'
          disabled={isDisabled}
        />
      ),
      pasteConfig: {
        lookupKey: 'ma_khon',
        resultField: 'ma_khon_data',
        detailEndpoint: QUERY_KEYS.KHO_HANG
      }
    },
    // Vị trí fields
    {
      field: 'ma_vi_tri',
      headerName: 'Từ vị trí',
      width: 150,
      renderCell: (params: DetailCellParams) => (
        <SearchField<ViTriKho>
          type='text'
          searchEndpoint={`/${QUERY_KEYS.VI_TRI_KHO_HANG}/`}
          searchColumns={viTriSearchColumns}
          columnDisplay='ma_vi_tri'
          dialogTitle='Danh mục vị trí kho'
          value={params.row.ma_vi_tri_data?.ma_vi_tri || ''}
          onRowSelection={(row: ViTriKho) => onCellValueChange(params.row.uuid, 'ma_vi_tri_data', row)}
          disabled={isDisabled || !params.row.ma_kho_data || !params.row.ma_khon_data}
          className='w-full'
        />
      ),
      pasteConfig: {
        lookupKey: 'ma_vi_tri',
        resultField: 'ma_vi_tri_data',
        detailEndpoint: QUERY_KEYS.VI_TRI_KHO_HANG
      }
    },
    // Vị trí nhận fields
    {
      field: 'ma_vi_trin',
      headerName: 'Đến vị trí',
      width: 150,
      renderCell: (params: DetailCellParams) => (
        <SearchField<ViTriKho>
          type='text'
          searchEndpoint={`/${QUERY_KEYS.VI_TRI_KHO_HANG}/`}
          searchColumns={viTriSearchColumns}
          columnDisplay='ma_vi_tri'
          dialogTitle='Danh mục vị trí kho'
          value={params.row.ma_vi_trin_data?.ma_vi_tri || ''}
          onRowSelection={(row: ViTriKho) => onCellValueChange(params.row.uuid, 'ma_vi_trin_data', row)}
          disabled={isDisabled || !params.row.ma_khon_data}
          className='w-full'
        />
      ),
      pasteConfig: {
        lookupKey: 'ma_vi_trin',
        resultField: 'ma_vi_trin_data',
        detailEndpoint: QUERY_KEYS.VI_TRI_KHO_HANG
      }
    },
    {
      field: 'sl_ton',
      headerName: 'Tồn',
      width: 100,
      renderCell: (params: DetailCellParams) => (
        <CellField name='sl_ton' type='number' value={params.row.ma_vt_data?.sl_ton || 0} disabled={isDisabled} />
      )
    },
    {
      field: 'so_luong',
      headerName: 'Số lượng',
      width: 140,
      renderCell: (params: DetailCellParams) => (
        <CellField
          name='so_luong'
          type='number'
          value={params.row.so_luong}
          onValueChange={newValue => onCellValueChange(params.row.uuid, 'so_luong', newValue)}
          disabled={isDisabled}
        />
      )
    },
    {
      field: 'px_dd',
      headerName: 'Đích danh',
      width: 100,
      type: 'boolean',
      align: 'center',
      renderCell: (params: DetailCellParams) => (
        <Checkbox
          checked={Boolean(params.row.px_dd)}
          onCheckedChange={newValue => onCellValueChange(params.row.uuid, 'px_dd', newValue)}
          disabled={isDisabled}
        />
      )
    },

    // Price fields
    {
      field: 'gia_nt',
      headerName: 'Giá VND',
      width: 170,
      renderCell: (params: DetailCellParams) => (
        <CellField
          name='gia_nt'
          type='number'
          value={params.row.gia_nt}
          onValueChange={newValue => onCellValueChange(params.row.uuid, 'gia_nt', newValue)}
          disabled={isDisabled || !params.row.px_dd}
        />
      )
    },
    {
      field: 'tien_nt',
      headerName: 'Tiền VND',
      width: 180,
      renderCell: (params: DetailCellParams) => (
        <CellField
          name='tien_nt'
          type='number'
          value={params.row.tien_nt}
          onValueChange={newValue => onCellValueChange(params.row.uuid, 'tien_nt', newValue)}
          disabled={isDisabled || !params.row.px_dd}
        />
      )
    },
    {
      field: 'tk_vt',
      headerName: 'TK có',
      width: 150,
      renderCell: (params: DetailCellParams) => (
        <SearchField<AccountModel>
          type='text'
          searchEndpoint={`/${QUERY_KEYS.TAI_KHOAN}/`}
          searchColumns={accountSearchColumns}
          columnDisplay='code'
          dialogTitle='Danh mục tài khoản'
          value={params.row.tk_vt_data?.code || ''}
          onRowSelection={(row: AccountModel) => onCellValueChange(params.row.uuid, 'tk_vt_data', row)}
          className='w-full'
          disabled={isDisabled}
        />
      )
    },
    // Nhập xuất fields
    {
      field: 'ma_nx',
      headerName: 'Lý do đ/c',
      width: 150,
      renderCell: (params: DetailCellParams) => (
        <SearchField<NhapXuat>
          type='text'
          searchEndpoint={`/${QUERY_KEYS.LY_DO_NHAP_XUAT}/`}
          searchColumns={lyDoNhapXuatSearchColumns}
          columnDisplay='ma_nx'
          dialogTitle='Danh mục lý do nhập xuất'
          value={params.row.ma_nx_data?.ma_nx || ''}
          onRowSelection={(row: NhapXuat) => onCellValueChange(params.row.uuid, 'ma_nx_data', row)}
          className='w-full'
          disabled={isDisabled}
        />
      ),
      pasteConfig: {
        lookupKey: 'ma_nx',
        resultField: 'ma_nx_data',
        detailEndpoint: QUERY_KEYS.LY_DO_NHAP_XUAT
      }
    },
    // Tài khoản dự fields
    {
      field: 'tk_du',
      headerName: 'Tk nợ',
      width: 150,
      renderCell: (params: DetailCellParams) => (
        <SearchField<AccountModel>
          type='text'
          searchEndpoint={`/${QUERY_KEYS.TAI_KHOAN}/`}
          searchColumns={accountSearchColumns}
          columnDisplay='code'
          dialogTitle='Danh mục tài khoản'
          value={params.row.tk_du_data?.code || ''}
          onRowSelection={(row: AccountModel) => onCellValueChange(params.row.uuid, 'tk_du_data', row)}
          className='w-full'
          disabled={isDisabled}
        />
      ),
      pasteConfig: {
        lookupKey: 'tk_du',
        resultField: 'tk_du_data',
        detailEndpoint: QUERY_KEYS.TAI_KHOAN
      }
    },
    // Ghi chú
    {
      field: 'ghi_chu',
      headerName: 'Ghi chú/Tên hàng hoá HĐĐT',
      width: 200,
      renderCell: (params: DetailCellParams) => (
        <CellField
          name='ghi_chu'
          type='text'
          value={params.row.ghi_chu || ''}
          onValueChange={newValue => onCellValueChange(params.row.uuid, 'ghi_chu', newValue)}
          disabled={isDisabled}
        />
      )
    },
    // Bộ phận
    {
      field: 'ma_bp',
      headerName: 'Bộ phận',
      width: 150,
      renderCell: (params: DetailCellParams) => (
        <SearchField<BoPhan>
          type='text'
          searchEndpoint={`/${QUERY_KEYS.BO_PHAN}/`}
          searchColumns={boPhanSearchColumns}
          columnDisplay='ma_bp'
          dialogTitle='Danh mục bộ phận'
          value={params.row.ma_bp_data?.ma_bp || ''}
          onRowSelection={(row: BoPhan) => onCellValueChange(params.row.uuid, 'ma_bp_data', row)}
          className='w-full'
          disabled={isDisabled}
        />
      ),
      pasteConfig: {
        lookupKey: 'ma_bp',
        resultField: 'ma_bp_data',
        detailEndpoint: QUERY_KEYS.BO_PHAN
      }
    },
    // Vụ việc
    {
      field: 'ma_vv',
      headerName: 'Vụ việc',
      width: 150,
      renderCell: (params: DetailCellParams) => (
        <SearchField<VuViec>
          type='text'
          searchEndpoint={`/${QUERY_KEYS.VU_VIEC}/`}
          searchColumns={vuViecSearchColumns}
          columnDisplay='ma_vu_viec'
          dialogTitle='Danh mục vụ việc'
          value={params.row.ma_vv_data?.ma_vu_viec || ''}
          onRowSelection={(row: VuViec) => onCellValueChange(params.row.uuid, 'ma_vv_data', row)}
          className='w-full'
          disabled={isDisabled}
        />
      ),
      pasteConfig: {
        lookupKey: 'ma_vv',
        resultField: 'ma_vv_data',
        detailEndpoint: QUERY_KEYS.VU_VIEC
      }
    },
    // Hợp đồng
    {
      field: 'ma_hd',
      headerName: 'Hợp đồng',
      width: 150,
      renderCell: (params: DetailCellParams) => (
        <SearchField<HopDong>
          type='text'
          searchEndpoint={`/${QUERY_KEYS.HOP_DONG}/`}
          searchColumns={hopDongSearchColumns}
          columnDisplay='ma_hd'
          dialogTitle='Danh mục hợp đồng'
          value={params.row.ma_hd_data?.ma_hd || ''}
          onRowSelection={(row: HopDong) => onCellValueChange(params.row.uuid, 'ma_hd_data', row)}
          className='w-full'
          disabled={isDisabled}
        />
      ),
      pasteConfig: {
        lookupKey: 'ma_hd',
        resultField: 'ma_hd_data',
        detailEndpoint: QUERY_KEYS.HOP_DONG
      }
    },
    // Đợt thanh toán
    {
      field: 'ma_dtt',
      headerName: 'Đợt thanh toán',
      width: 150,
      renderCell: (params: DetailCellParams) => (
        <SearchField<DotThanhToan>
          type='text'
          searchEndpoint={`/${QUERY_KEYS.DOT_THANH_TOAN}/`}
          searchColumns={dotThanhToanSearchColumns}
          columnDisplay='ma_dtt'
          dialogTitle='Danh mục đợt thanh toán'
          value={params.row.ma_dtt_data?.ma_dtt || ''}
          onRowSelection={(row: DotThanhToan) => onCellValueChange(params.row.uuid, 'ma_dtt_data', row)}
          className='w-full'
          disabled={isDisabled}
        />
      ),
      pasteConfig: {
        lookupKey: 'ma_dtt',
        resultField: 'ma_dtt_data',
        detailEndpoint: QUERY_KEYS.DOT_THANH_TOAN
      }
    },
    // Khế ước
    {
      field: 'ma_ku',
      headerName: 'Khế ước',
      width: 150,
      renderCell: (params: DetailCellParams) => (
        <SearchField<KheUoc>
          type='text'
          searchEndpoint={`/${QUERY_KEYS.KHE_UOC}/`}
          searchColumns={kheUocSearchColumns}
          columnDisplay='ma_ku'
          dialogTitle='Danh mục khế ước'
          value={params.row.ma_ku_data?.ma_ku || ''}
          onRowSelection={(row: KheUoc) => onCellValueChange(params.row.uuid, 'ma_ku_data', row)}
          className='w-full'
          disabled={isDisabled}
        />
      ),
      pasteConfig: {
        lookupKey: 'ma_ku',
        resultField: 'ma_ku_data',
        detailEndpoint: QUERY_KEYS.KHE_UOC
      }
    },
    // Phí
    {
      field: 'ma_phi',
      headerName: 'Phí',
      width: 150,
      renderCell: (params: DetailCellParams) => (
        <SearchField<Phi>
          type='text'
          searchEndpoint={`/${QUERY_KEYS.PHI}/`}
          searchColumns={phiSearchColumns}
          columnDisplay='ma_phi'
          dialogTitle='Danh mục phí'
          value={params.row.ma_phi_data?.ma_phi || ''}
          onRowSelection={(row: Phi) => onCellValueChange(params.row.uuid, 'ma_phi_data', row)}
          className='w-full'
          disabled={isDisabled}
        />
      ),
      pasteConfig: {
        lookupKey: 'ma_phi',
        resultField: 'ma_phi_data',
        detailEndpoint: QUERY_KEYS.PHI
      }
    },
    // Sản phẩm
    {
      field: 'ma_sp',
      headerName: 'Sản phẩm',
      width: 150,
      renderCell: (params: DetailCellParams) => (
        <SearchField<VatTu>
          type='text'
          searchEndpoint={`/${QUERY_KEYS.VAT_TU}/`}
          searchColumns={vatTu1SearchColumns}
          columnDisplay='ma_vt'
          dialogTitle='Danh mục sản phẩm'
          value={params.row.ma_sp_data?.ma_vt || ''}
          onRowSelection={(row: VatTu) => onCellValueChange(params.row.uuid, 'ma_sp_data', row)}
          className='w-full'
          disabled={isDisabled}
        />
      ),
      pasteConfig: {
        lookupKey: 'ma_sp',
        resultField: 'ma_sp_data',
        detailEndpoint: QUERY_KEYS.VAT_TU
      }
    },
    // Lệnh sản xuất
    {
      field: 'ma_lsx',
      headerName: 'Lệnh sản xuất',
      width: 150,
      renderCell: (params: DetailCellParams) => (
        <SearchField<PhieuXuatDieuChuyenLenhSanXuatData>
          type='text'
          searchEndpoint={'/'}
          searchColumns={lenhSanXuatSearchColumns}
          columnDisplay='ma_lsx'
          dialogTitle='Danh mục lệnh sản xuất'
          value={params.row.ma_lsx_data?.ma_lsx || ''}
          onRowSelection={(row: PhieuXuatDieuChuyenLenhSanXuatData) =>
            onCellValueChange(params.row.uuid, 'ma_lsx_data', row)
          }
          className='w-full'
          disabled={isDisabled}
        />
      ),
      pasteConfig: {
        lookupKey: 'ma_lsx',
        resultField: 'ma_lsx_data',
        detailEndpoint: QUERY_KEYS.LENH_SAN_XUAT
      }
    },
    // Chi phí không hợp lệ
    {
      field: 'ma_cp0',
      headerName: 'C/p không h/lệ',
      width: 150,
      renderCell: (params: DetailCellParams) => (
        <SearchField<ChiPhiKhongHopLeData>
          type='text'
          searchEndpoint={`/${QUERY_KEYS.CHI_PHI_KHONG_HOP_LE}/`}
          searchColumns={chiPhiKhongHopLeSearchColumns}
          columnDisplay='ma_cp_khl'
          dialogTitle='Danh mục chi phí không hợp lệ'
          value={params.row.ma_cp0_data?.ma_cpkhl || ''}
          onRowSelection={(row: ChiPhiKhongHopLeData) => onCellValueChange(params.row.uuid, 'ma_cp0_data', row)}
          className='w-full'
          disabled={isDisabled}
        />
      ),
      pasteConfig: {
        lookupKey: 'ma_cp0',
        resultField: 'ma_cp0_data',
        detailEndpoint: QUERY_KEYS.CHI_PHI_KHONG_HOP_LE
      }
    },
    {
      field: 'gia',
      headerName: 'Giá',
      width: 150,
      renderCell: (params: DetailCellParams) => (
        <CellField name='gia' type='text' value={params.row.gia || ''} disabled={isDisabled} />
      )
    },
    {
      field: 'tien',
      headerName: 'Tiền',
      width: 150,
      renderCell: (params: DetailCellParams) => (
        <CellField name='tien' type='text' value={params.row.tien || ''} disabled={isDisabled} />
      )
    },
    {
      field: 'so_ct_pn',
      headerName: 'Số CT PN',
      width: 150,
      renderCell: (params: DetailCellParams) => (
        <CellField name='so_ct_pn' type='text' value={params.row.so_ct_pn || ''} disabled={isDisabled} />
      )
    }
  ];
};
